/**
 * Shared utility for processing fisheries time series data.
 * Used by StockDataProvider (Stock page) and PlanDataProvider (Plan page).
 *
 * @param allYearData - Array of { year, data } where data is the raw fishery records for that year
 * @param stockId - null = aggregate all stocks; number = single stock (Stock page only). Plan always passes null.
 * @param includeMeals - When false (Plan), meals array is empty. Stock passes true.
 * @param stockUuid - Optional UUID for single-stock matching when API returns records keyed by stock_uuid.
 */
export function processTimeSeriesData(
  allYearData: Array<{ year: number; data: unknown[] }>,
  stockId: number | null,
  includeMeals = true,
  stockUuid?: string | null
): {
  biomass: Array<Record<string, unknown>>;
  impact: Array<Record<string, unknown>>;
  meals: Array<Record<string, unknown>>;
  exvessel: Array<Record<string, unknown>>;
} {
  const biomassData: Array<Record<string, unknown>> = [];
  const impactData: Array<Record<string, unknown>> = [];
  const mealsData: Array<Record<string, unknown>> = [];
  const exVesselData: Array<Record<string, unknown>> = [];

  allYearData.forEach((yearData) => {
    const { year, data } = yearData;
    if (!data?.length) return;

    const items = data as Array<Record<string, unknown>>;

    let relevantData: Record<string, number> | null = null;

    if (stockId === null) {
      // Aggregate across all stocks
      const biomass = items.reduce(
        (sum, item) =>
          sum + (Number(item.adjusted_biomass) || Number(item.original_biomass) || 0),
        0
      );
      const ofl = items.reduce((sum, item) => sum + (Number(item.mean_ofl) || 0), 0);
      const abc = items.reduce((sum, item) => sum + (Number(item.mean_abc) || 0), 0);
      const landings = items.reduce(
        (sum, item) => sum + (Number(item.landings_value) || 0),
        0
      );
      const acl = items.reduce((sum, item) => sum + (Number(item.mean_acl) || 0), 0);
      const actualExVessel = items.reduce(
        (sum, item) => sum + (Number(item.mean_ex_vessel) || 0),
        0
      );
      const uncaughtExVessel = items.reduce(
        (sum, item) => sum + (Number(item.mean_uncaught_ex_vessel) || 0),
        0
      );
      const actualEconomicImpact = items.reduce(
        (sum, item) => sum + (Number(item.landed_economic_impact) || 0),
        0
      );
      const uncaughtEconomicImpact = items.reduce(
        (sum, item) => sum + (Number(item.uncaught_economic_impact) || 0),
        0
      );
      const actualMeals = items.reduce(
        (sum, item) => sum + (Number(item.meals_produced) || 0),
        0
      );

      const biomassUncertainty = Math.sqrt(
        items.reduce(
          (sum, item) => sum + Math.pow(Number(item.biomass_uncertainty) || 0, 2),
          0
        )
      );
      const oflUncertainty = Math.sqrt(
        items.reduce(
          (sum, item) => sum + Math.pow(Number(item.ofl_uncertainty) || 0, 2),
          0
        )
      );
      const abcUncertainty = Math.sqrt(
        items.reduce(
          (sum, item) => sum + Math.pow(Number(item.abc_uncertainty) || 0, 2),
          0
        )
      );
      const landingsUncertainty = Math.sqrt(
        items.reduce(
          (sum, item) => sum + Math.pow(Number(item.landings_uncertainty) || 0, 2),
          0
        )
      );
      const aclUncertainty = Math.sqrt(
        items.reduce(
          (sum, item) => sum + Math.pow(Number(item.acl_uncertainty) || 0, 2),
          0
        )
      );
      const exVesselUncertainty = Math.sqrt(
        items.reduce(
          (sum, item) => sum + Math.pow(Number(item.ex_vessel_uncertainty) || 0, 2),
          0
        )
      );
      const uncaughtExVesselUncertainty = Math.sqrt(
        items.reduce(
          (sum, item) =>
            sum + Math.pow(Number(item.uncaught_ex_vessel_uncertainty) || 0, 2),
          0
        )
      );
      const actualEconomicImpactUncertainty = Math.sqrt(
        items.reduce(
          (sum, item) =>
            sum + Math.pow(Number(item.landed_economic_impact_uncertainty) || 0, 2),
          0
        )
      );
      const uncaughtEconomicImpactUncertainty = Math.sqrt(
        items.reduce(
          (sum, item) =>
            sum + Math.pow(Number(item.uncaught_economic_impact_uncertainty) || 0, 2),
          0
        )
      );
      const mealsUncertainty = Math.sqrt(
        items.reduce(
          (sum, item) => sum + Math.pow(Number(item.meals_uncertainty) || 0, 2),
          0
        )
      );

      let uncaughtMeals = 0;
      let uncaughtMealsUncertainty = 0;
      items.forEach((item) => {
        const caughtWeight = Number(item.landings_value) || 0;
        const uncaughtWeight = Number(item.mean_uncaught_acl) || 0;
        const mealsProduced = Number(item.meals_produced) || 0;
        const mealsUnc = Number(item.meals_uncertainty) || 0;
        if (caughtWeight > 0 && mealsProduced) {
          const ratio = uncaughtWeight / caughtWeight;
          uncaughtMeals += mealsProduced * ratio;
          uncaughtMealsUncertainty += Math.pow(mealsUnc * ratio, 2);
        }
      });
      uncaughtMealsUncertainty = Math.sqrt(uncaughtMealsUncertainty);

      relevantData = {
        biomass,
        ofl,
        abc,
        landings,
        acl,
        biomassUncertainty,
        oflUncertainty,
        abcUncertainty,
        landingsUncertainty,
        aclUncertainty,
        actualExVessel,
        uncaughtExVessel,
        actualEconomicImpact,
        uncaughtEconomicImpact,
        actualMeals,
        uncaughtMeals,
        exVesselUncertainty,
        uncaughtExVesselUncertainty,
        actualEconomicImpactUncertainty,
        uncaughtEconomicImpactUncertainty,
        mealsUncertainty,
        uncaughtMealsUncertainty,
      };
    } else {
      // Single stock - match by stock_id, fishery_management_stock_id, or stock_uuid (with type coercion)
      // When API returns pre-filtered data (one stock per request), items may have only one record
      let stockData = items.find((item) => {
        const itemRecord = item as Record<string, unknown>;
        const itemStockId = itemRecord.stock_id ?? itemRecord.fishery_management_stock_id;
        const matchesId =
          stockId != null &&
          itemStockId != null &&
          Number(itemStockId) === Number(stockId);
        const matchesUuid =
          stockUuid != null &&
          (itemRecord.stock_uuid === stockUuid ||
            String(itemRecord.stock_uuid) === String(stockUuid));
        return matchesId || matchesUuid;
      });
      // Fallback: when requesting single stock by UUID, backend may return one record per year with no stock_id
      if (!stockData && items.length === 1 && (stockId != null || stockUuid != null)) {
        stockData = items[0] as Record<string, unknown>;
      }
      if (!stockData) return;

      const caughtWeight = Number(stockData.landings_value) || 0;
      const uncaughtWeight = Number(stockData.mean_uncaught_acl) || 0;
      const mealsProduced = Number(stockData.meals_produced) || 0;
      const mealsUnc = Number(stockData.meals_uncertainty) || 0;
      let uncaughtMeals = 0;
      let uncaughtMealsUncertainty = 0;
      if (caughtWeight > 0 && mealsProduced) {
        const ratio = uncaughtWeight / caughtWeight;
        uncaughtMeals = mealsProduced * ratio;
        uncaughtMealsUncertainty = mealsUnc * ratio;
      }

      relevantData = {
        biomass:
          Number(stockData.adjusted_biomass) || Number(stockData.original_biomass) || 0,
        ofl: Number(stockData.mean_ofl) || 0,
        abc: Number(stockData.mean_abc) || 0,
        landings: Number(stockData.landings_value) || 0,
        acl: Number(stockData.mean_acl) || 0,
        biomassUncertainty: Number(stockData.biomass_uncertainty) || 0,
        oflUncertainty: Number(stockData.ofl_uncertainty) || 0,
        abcUncertainty: Number(stockData.abc_uncertainty) || 0,
        landingsUncertainty: Number(stockData.landings_uncertainty) || 0,
        aclUncertainty: Number(stockData.acl_uncertainty) || 0,
        actualExVessel: Number(stockData.mean_ex_vessel) || 0,
        uncaughtExVessel: Number(stockData.mean_uncaught_ex_vessel) || 0,
        actualEconomicImpact: Number(stockData.landed_economic_impact) || 0,
        uncaughtEconomicImpact: Number(stockData.uncaught_economic_impact) || 0,
        actualMeals: mealsProduced,
        uncaughtMeals,
        exVesselUncertainty: Number(stockData.ex_vessel_uncertainty) || 0,
        uncaughtExVesselUncertainty:
          Number(stockData.uncaught_ex_vessel_uncertainty) || 0,
        actualEconomicImpactUncertainty:
          Number(stockData.landed_economic_impact_uncertainty) || 0,
        uncaughtEconomicImpactUncertainty:
          Number(stockData.uncaught_economic_impact_uncertainty) || 0,
        mealsUncertainty: mealsUnc,
        uncaughtMealsUncertainty,
      };
    }

    if (relevantData) {
      biomassData.push({
        year,
        biomass: relevantData.biomass,
        ofl: relevantData.ofl,
        abc: relevantData.abc,
        acl: relevantData.acl,
        landings: relevantData.landings,
        biomassUncertainty: relevantData.biomassUncertainty,
        oflUncertainty: relevantData.oflUncertainty,
        abcUncertainty: relevantData.abcUncertainty,
        aclUncertainty: relevantData.aclUncertainty,
        landingsUncertainty: relevantData.landingsUncertainty,
      });

      impactData.push({
        year,
        actual: relevantData.actualEconomicImpact,
        potential: relevantData.actualEconomicImpact + relevantData.uncaughtEconomicImpact,
        actualUncertainty: relevantData.actualEconomicImpactUncertainty,
        potentialUncertainty: Math.sqrt(
          Math.pow(relevantData.actualEconomicImpactUncertainty, 2) +
            Math.pow(relevantData.uncaughtEconomicImpactUncertainty, 2)
        ),
      });

      if (includeMeals) {
        mealsData.push({
          year,
          actual: relevantData.actualMeals,
          potential: relevantData.actualMeals + relevantData.uncaughtMeals,
          actualUncertainty: relevantData.mealsUncertainty,
          potentialUncertainty: Math.sqrt(
            Math.pow(relevantData.mealsUncertainty, 2) +
              Math.pow(relevantData.uncaughtMealsUncertainty, 2)
          ),
        });
      }

      exVesselData.push({
        year,
        actual: relevantData.actualExVessel,
        potential: relevantData.actualExVessel + relevantData.uncaughtExVessel,
        actualUncertainty: relevantData.exVesselUncertainty,
        potentialUncertainty: Math.sqrt(
          Math.pow(relevantData.exVesselUncertainty, 2) +
            Math.pow(relevantData.uncaughtExVesselUncertainty, 2)
        ),
      });
    }
  });

  const sortByYear = (
    a: Record<string, unknown>,
    b: Record<string, unknown>
  ) => (a.year as number) - (b.year as number);
  biomassData.sort(sortByYear);
  impactData.sort(sortByYear);
  mealsData.sort(sortByYear);
  exVesselData.sort(sortByYear);

  return {
    biomass: biomassData,
    impact: impactData,
    meals: mealsData,
    exvessel: exVesselData,
  };
}
